import {
  Component,
  OnInit,
  OnDestroy,
  ViewChildren,
  AfterViewInit,
  QueryList
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ToastrService, ActiveToast } from 'ngx-toastr';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import {
  AlertService,
  AlertDialog,
  DialogType,
  AlertCommand,
  MessageSeverity
} from '../services/alert.service';

import { NotificationService } from '../services/notification.service';
import { AppTranslationService } from '../services/app-translation.service';
import { AccountService } from '../services/account.service';
import { LocalStoreManager } from '../services/local-store-manager.service';
import { AppTitleService } from '../services/app-title.service';
import { AuthService } from '../services/auth.service';
import { ConfigurationService } from '../services/configuration.service';
import { Permission } from '../models/permission.model';
import { LoginComponent } from '../components/login/login.component';
import { AiHintStateService } from '../services/ai-hint-state.service';
import { HubReportStatusService } from '../services/hub-report-status.service';

const alertify: any = require('../assets/scripts/alertify.js');

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

  private destroy$ = new Subject<void>();

  isAppLoaded!: boolean;
  isUserLoggedIn!: boolean;
  showAiPanel = false;
  /** Mobile off-canvas sidebar visibility (only takes effect below 992px). */
  sidebarOpen = false;
  shouldShowLoginModal!: boolean;
  newNotificationCount = 0;
  appTitle = 'BRIDGE';
  /** Feature 9 — true when the logged-in hub still owes the current week's report (shows a menu badge). */
  hubReportDue = false;
  /** True once the report is due and we're on day 4 (Thursday) or later — flashes the menu badge. */
  hubReportUrgent = false;

  /** ngx-toastr sticky references */
  private stickyToasts: ActiveToast<any>[] = [];

  dataLoadingConsecutiveFailures = 0;
  notificationsLoadingSubscription: any;

  @ViewChildren('loginModal,loginControl')
  modalLoginControls!: QueryList<any>;

  loginModal!: ModalDirective;
  loginControl!: LoginComponent;

  gT = (key: string | string[], interpolateParams?: object) =>
    this.translationService.getTranslation(key, interpolateParams);

  constructor(
    storageManager: LocalStoreManager,
    private toastr: ToastrService,
    private accountService: AccountService,
    private alertService: AlertService,
    private notificationService: NotificationService,
    private appTitleService: AppTitleService,
    private authService: AuthService,
    private translationService: AppTranslationService,
    public configurations: ConfigurationService,
    public router: Router,
    private hintState: AiHintStateService,
    private hubReportStatus: HubReportStatusService
  ) {
    storageManager.initialiseStorageSyncListener();
    this.appTitleService.appName = this.appTitle;
  }

  /* ---------------------------------- impersonation banner ---------------------------------- */

  get isImpersonating(): boolean {
    return this.authService.isImpersonating;
  }

  get impersonatedUserName(): string {
    const u = this.authService.currentUser;
    return u ? (u.fullName || u.userName) : '';
  }

  get impersonatorName(): string {
    const a = this.authService.impersonatorUser;
    return a ? (a.fullName || a.userName) : 'your admin account';
  }

  stopImpersonation(): void {
    if (this.authService.stopImpersonation()) {
      // Full reload so all role-gated UI reverts to the admin session.
      window.location.href = this.configurations.homeUrl || '/';
    }
  }

  /* ---------------------------------- lifecycle ---------------------------------- */

  private isFormRoute(url: string): boolean {
    return url === '/icd' || url.startsWith('/icddetail/');
  }

  ngOnInit() {
    this.isUserLoggedIn = this.authService.isLoggedIn;

    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((e: any) => {
      this.showAiPanel = this.isFormRoute(e.urlAfterRedirects);
      this.hintState.clear();
      // Auto-close the mobile menu after navigating so it never obscures the page.
      this.sidebarOpen = false;
    });

     if (this.isUserLoggedIn) {
    this.authService['startTokenExpiryMonitor']();
  }
    setTimeout(() => (this.isAppLoaded = true), 1000);

    // Feature 9 — reflect "weekly report due" as a menu badge; refresh on login.
    this.hubReportStatus.reportDue.pipe(takeUntil(this.destroy$)).subscribe(due => this.hubReportDue = due);
    this.hubReportStatus.reportUrgent.pipe(takeUntil(this.destroy$)).subscribe(urgent => this.hubReportUrgent = urgent);
    if (this.isUserLoggedIn) { this.hubReportStatus.refresh(); }

    this.alertService.getDialogEvent().pipe(takeUntil(this.destroy$)).subscribe(d => this.showDialog(d));
    this.alertService.getMessageEvent().pipe(takeUntil(this.destroy$)).subscribe(m => this.showToast(m));

    this.authService.reLoginDelegate = () => (this.shouldShowLoginModal = true);

    this.authService.getLoginStatusEvent().pipe(takeUntil(this.destroy$)).subscribe(isLoggedIn => {
      this.isUserLoggedIn = isLoggedIn;
      /*isLoggedIn ? this.initNotificationsLoading() : this.unsubscribeNotifications();*/

      if (this.isUserLoggedIn && this.newNotificationCount === 0) {
        this.initNotificationsLoading();
      }

      if (this.isUserLoggedIn) { this.hubReportStatus.refresh(); }

      setTimeout(() => {
        if (!this.isUserLoggedIn) {
          this.alertService.showMessage('Session Ended!', '', MessageSeverity.default);
        }
      }, 500);

    });
  }

  ngAfterViewInit() {
    this.modalLoginControls.changes.pipe(takeUntil(this.destroy$)).subscribe(controls => {
      controls.forEach(control => {
        if (control instanceof LoginComponent) {
          this.loginControl = control;
          this.loginControl.modalClosedCallback = () => this.loginModal.hide();
        } else {
          this.loginModal = control;
          this.loginModal.show();
        }
      });
    });
  }

  //ngOnDestroy() {
  //  this.unsubscribeNotifications();
  //}

  /* ---------------------------------- mobile sidebar ---------------------------------- */

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
  }

  /* ---------------------------------- dialogs ---------------------------------- */

  showDialog(dialog: AlertDialog) {
    // alertify v1.x removed .set() — guard for compatibility
    if (typeof (alertify as any).set === 'function') {
      (alertify as any).set({
        labels: {
          ok: dialog.okLabel || 'OK',
          cancel: dialog.cancelLabel || 'Cancel'
        }
      });
    }

    switch (dialog.type) {
      case DialogType.alert:
        alertify.alert(dialog.message);
        break;

      case DialogType.confirm:
        alertify.confirm(dialog.message, (e: boolean) =>
          e ? dialog.okCallback() : dialog.cancelCallback?.()
        );
        break;

      case DialogType.prompt:
        alertify.prompt(
          dialog.message,
          (e: boolean, val: string) =>
            e ? dialog.okCallback(val) : dialog.cancelCallback?.(),
          dialog.defaultValue
        );
        break;
    }
  }

  /* ---------------------------------- TOASTS (FIXED) ---------------------------------- */

  showToast(alert: AlertCommand) {

    /* CLEAR */
    if (alert.operation === 'clear') {
      this.stickyToasts.forEach(t => this.toastr.clear(t.toastId));
      this.stickyToasts = [];
      return;
    }

    const title = alert.message?.summary;
    const message = alert.message?.detail;

    const options = {
      closeButton: true,
      progressBar: false,
      timeOut: alert.operation === 'add_sticky' ? 0 : 4000,
      disableTimeOut: alert.operation === 'add_sticky',
      tapToDismiss: alert.operation !== 'add_sticky'
    };

    let toast: ActiveToast<any> | null = null;

    switch (alert.message.severity) {
      case MessageSeverity.success:
        toast = this.toastr.success(message, title, options);
        break;
      case MessageSeverity.error:
        toast = this.toastr.error(message, title, options);
        break;
      case MessageSeverity.warn:
        toast = this.toastr.warning(message, title, options);
        break;
      case MessageSeverity.info:
        toast = this.toastr.info(message, title, options);
        break;
      default:
        toast = this.toastr.show(message, title, options);
        break;
    }

    /* STICKY HANDLING */
    if (toast && alert.operation === 'add_sticky') {
      this.stickyToasts.push(toast);

      toast.onHidden.subscribe(() => {
        this.stickyToasts = this.stickyToasts.filter(t => t !== toast);
        alert.onRemove?.();
      });
    }
  }

  /* ---------------------------------- notifications ---------------------------------- */

  initNotificationsLoading() {
    this.notificationsLoadingSubscription =
      this.notificationService.getNewNotificationsPeriodically().pipe(takeUntil(this.destroy$)).subscribe(
        notifications =>
          (this.newNotificationCount = notifications.filter(n => !n.isRead).length),
        error => {
          this.alertService.logError(error);
          if (this.dataLoadingConsecutiveFailures++ < 20) {
            setTimeout(() => this.initNotificationsLoading(), 5000);
          } else {
            this.alertService.showStickyMessage(
              'Load Error',
              'Loading notifications failed!',
              MessageSeverity.error
            );
          }
        }
      );
  }

  //unsubscribeNotifications() {
  //  this.notificationsLoadingSubscription?.unsubscribe();
  //}

  /* ---------------------------------- helpers ---------------------------------- */

  logout() {
    this.authService.logout();
    this.authService.redirectLogoutUser();
  }

  get userName() {
    return this.authService.currentUser?.userName ?? '';
  }

  get fullName() {
    return this.authService.currentUser?.fullName ?? '';
  }

  getYear() {
    return new Date().getUTCFullYear();
  }

  get canViewCustomers() {
    return this.accountService.userHasPermission(Permission.viewUsersPermission);
  }

  // Feature 9 — hubs (and admins) can upload the weekly ICD report CSV.
  get isHubUser() {
    const roles = this.accountService.currentUser?.roles || [];
    return roles.includes('Hub') || roles.includes('HubAssistant') || roles.includes('administrator');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // app.component.ts
  onLoginModalShown(): void { }

  onLoginModalHidden(): void { }

  onLoginModalHide(): void { }

}
